import React, { useState, useEffect } from 'react';
import { perksAPI } from '../../lib/api';
import { GlassCard, StatCard } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus,
  Gift,
  Edit2,
  Trash2,
  BarChart3,
  Eye,
  Tag,
  MapPin,
  Calendar,
  Users,
  TrendingUp,
  Star,
  StarOff,
} from 'lucide-react';

const CATEGORIES = [
  { id: 'fnb', name: 'Food & Beverage' },
  { id: 'shopping', name: 'Shopping' },
  { id: 'entertainment', name: 'Entertainment' },
  { id: 'travel', name: 'Travel & Hotels' },
  { id: 'health', name: 'Health & Wellness' },
  { id: 'services', name: 'Services' },
  { id: 'automotive', name: 'Automotive' },
  { id: 'education', name: 'Education' },
  { id: 'other', name: 'Other' },
];

const PERK_TYPES = [
  { id: 'discount', name: 'Discount' },
  { id: 'voucher', name: 'Voucher' },
  { id: 'freebie', name: 'Freebie' },
  { id: 'cashback', name: 'Cashback' },
  { id: 'exclusive_access', name: 'Exclusive Access' },
];

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const EMPTY_FORM = {
  title: '',
  description: '',
  merchant_name: '',
  category: 'fnb',
  perk_type: 'discount',
  discount_value: '',
  terms_conditions: '',
  location: { address: '', city: '', state: '' },
  valid_from: '',
  valid_until: '',
  eligible_roles: ['customer', 'agent'],
  is_featured: false,
  is_active: true,
  tags: [],
};

export default function AdminPerks() {
  const [perks, setPerks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPerk, setEditingPerk] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [tab, setTab] = useState('list');

  const loadPerks = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCat) params.category = filterCat;
      const res = await perksAPI.adminList(params);
      setPerks(res.data.perks || []);
    } catch {
      toast.error('Failed to load perks');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await perksAPI.adminGetStats();
      setStats(res.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadPerks(); loadStats(); }, [filterCat]);

  const openCreateForm = () => {
    setEditingPerk(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEditForm = (perk) => {
    setEditingPerk(perk);
    setForm({
      title: perk.title || '',
      description: perk.description || '',
      merchant_name: perk.merchant_name || '',
      category: perk.category || 'fnb',
      perk_type: perk.perk_type || 'discount',
      discount_value: perk.discount_value || '',
      terms_conditions: perk.terms_conditions || '',
      location: perk.location || { address: '', city: '', state: '' },
      valid_from: perk.valid_from ? new Date(perk.valid_from).toISOString().split('T')[0] : '',
      valid_until: perk.valid_until ? new Date(perk.valid_until).toISOString().split('T')[0] : '',
      eligible_roles: perk.eligible_roles || ['customer', 'agent'],
      is_featured: perk.is_featured || false,
      is_active: perk.is_active !== false,
      tags: perk.tags || [],
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.description || !form.merchant_name || !form.terms_conditions || !form.valid_from || !form.valid_until) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        valid_from: new Date(form.valid_from).toISOString(),
        valid_until: new Date(form.valid_until).toISOString(),
      };
      if (editingPerk) {
        await perksAPI.adminUpdate(editingPerk.id, payload);
        toast.success('Perk updated!');
      } else {
        await perksAPI.adminCreate(payload);
        toast.success('Perk created!');
      }
      setShowForm(false);
      loadPerks();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save perk');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (perk) => {
    if (!window.confirm(`Deactivate "${perk.title}"?`)) return;
    try {
      await perksAPI.adminDelete(perk.id);
      toast.success('Perk deactivated');
      loadPerks();
      loadStats();
    } catch {
      toast.error('Failed to deactivate');
    }
  };

  const toggleFeatured = async (perk) => {
    try {
      await perksAPI.adminUpdate(perk.id, { is_featured: !perk.is_featured });
      toast.success(perk.is_featured ? 'Removed from featured' : 'Added to featured');
      loadPerks();
    } catch {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-perks-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Perks & Benefits</h1>
          <p className="text-gray-500 mt-1">Manage promotions and merchant deals</p>
        </div>
        <Button onClick={openCreateForm} className="bg-[#4E50FF] hover:bg-[#3E40EE]" data-testid="create-perk-btn">
          <Plus className="w-4 h-4 mr-2" />New Perk
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard title="Total Perks" value={stats.total_perks} icon={Gift} />
          <StatCard title="Active Perks" value={stats.active_perks} icon={Tag} />
          <StatCard title="Total Uses" value={stats.total_uses} icon={TrendingUp} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        <button onClick={() => setTab('list')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
          All Perks
        </button>
        <button onClick={() => setTab('stats')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === 'stats' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
          Statistics
        </button>
      </div>

      {tab === 'list' && (
        <>
          {/* Category Filter */}
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setFilterCat('')} className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${!filterCat ? 'bg-[#4E50FF] text-white border-[#4E50FF]' : 'bg-white text-gray-600 border-gray-200'}`}>All</button>
            {CATEGORIES.map((c) => (
              <button key={c.id} onClick={() => setFilterCat(c.id)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${filterCat === c.id ? 'bg-[#4E50FF] text-white border-[#4E50FF]' : 'bg-white text-gray-600 border-gray-200'}`}>
                {c.name}
              </button>
            ))}
          </div>

          {/* Perks Table */}
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#4E50FF]" />
            </div>
          ) : perks.length === 0 ? (
            <GlassCard className="text-center py-12">
              <Gift className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No perks found</p>
              <Button onClick={openCreateForm} className="mt-4 bg-[#4E50FF] hover:bg-[#3E40EE]">Create First Perk</Button>
            </GlassCard>
          ) : (
            <div className="space-y-3" data-testid="admin-perks-list">
              {perks.map((perk) => (
                <GlassCard key={perk.id} className="flex items-center gap-4 p-4" data-testid={`admin-perk-${perk.id}`}>
                  <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Gift className="w-6 h-6 text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 truncate">{perk.title}</h3>
                      {!perk.is_active && <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full">Inactive</span>}
                      {perk.is_featured && <Star className="w-4 h-4 text-amber-400 fill-amber-400" />}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span>{perk.merchant_name}</span>
                      <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{perk.category}</span>
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" />{perk.total_uses || 0} uses</span>
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Until {formatDate(perk.valid_until)}</span>
                    </div>
                  </div>
                  {perk.discount_value && <span className="text-sm font-bold text-[#4E50FF] flex-shrink-0">{perk.discount_value}</span>}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => toggleFeatured(perk)} title={perk.is_featured ? 'Remove featured' : 'Set featured'}>
                      {perk.is_featured ? <StarOff className="w-4 h-4 text-amber-500" /> : <Star className="w-4 h-4 text-gray-400" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEditForm(perk)} data-testid={`edit-perk-${perk.id}`}>
                      <Edit2 className="w-4 h-4 text-gray-500" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(perk)} data-testid={`delete-perk-${perk.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </GlassCard>
              ))}
            </div>
          )}
        </>
      )}

      {/* Stats Tab */}
      {tab === 'stats' && stats && (
        <div className="space-y-6">
          {/* Top Perks */}
          <GlassCard>
            <h3 className="font-semibold text-gray-900 mb-4">Top Perks by Usage</h3>
            {stats.top_perks?.length > 0 ? (
              <div className="space-y-3">
                {stats.top_perks.map((p, i) => (
                  <div key={p.id} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#4E50FF]/10 text-[#4E50FF] text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{p.title}</p>
                      <p className="text-xs text-gray-500">{p.merchant_name}</p>
                    </div>
                    <span className="text-sm font-bold text-[#4E50FF]">{p.total_uses || 0}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No usage data yet</p>
            )}
          </GlassCard>

          {/* By Category */}
          <GlassCard>
            <h3 className="font-semibold text-gray-900 mb-4">Uses by Category</h3>
            {stats.by_category?.length > 0 ? (
              <div className="space-y-2">
                {stats.by_category.map((c) => (
                  <div key={c._id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 capitalize">{c._id}</span>
                    <span className="font-semibold text-gray-900">{c.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No usage data yet</p>
            )}
          </GlassCard>
        </div>
      )}

      {/* Create/Edit Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="perk-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingPerk ? 'Edit Perk' : 'Create New Perk'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. 20% Off at Sushi King" data-testid="perk-form-title" />
            </div>
            <div>
              <Label>Description *</Label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe the perk..." className="w-full border rounded-lg p-2.5 text-sm min-h-[80px] focus:ring-2 focus:ring-[#4E50FF]/20 focus:border-[#4E50FF] outline-none" data-testid="perk-form-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Merchant Name *</Label>
                <Input value={form.merchant_name} onChange={(e) => setForm({ ...form, merchant_name: e.target.value })} placeholder="e.g. Sushi King" data-testid="perk-form-merchant" />
              </div>
              <div>
                <Label>Discount Value</Label>
                <Input value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} placeholder="e.g. 20%, RM50" data-testid="perk-form-discount" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#4E50FF]/20 focus:border-[#4E50FF] outline-none" data-testid="perk-form-category">
                  {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Type *</Label>
                <select value={form.perk_type} onChange={(e) => setForm({ ...form, perk_type: e.target.value })} className="w-full border rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#4E50FF]/20 focus:border-[#4E50FF] outline-none" data-testid="perk-form-type">
                  {PERK_TYPES.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Terms & Conditions *</Label>
              <textarea value={form.terms_conditions} onChange={(e) => setForm({ ...form, terms_conditions: e.target.value })} placeholder="Enter terms..." className="w-full border rounded-lg p-2.5 text-sm min-h-[60px] focus:ring-2 focus:ring-[#4E50FF]/20 focus:border-[#4E50FF] outline-none" data-testid="perk-form-terms" />
            </div>
            <div>
              <Label>Location</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <Input value={form.location.address} onChange={(e) => setForm({ ...form, location: { ...form.location, address: e.target.value } })} placeholder="Address" data-testid="perk-form-address" />
                <Input value={form.location.city} onChange={(e) => setForm({ ...form, location: { ...form.location, city: e.target.value } })} placeholder="City" data-testid="perk-form-city" />
                <Input value={form.location.state} onChange={(e) => setForm({ ...form, location: { ...form.location, state: e.target.value } })} placeholder="State" data-testid="perk-form-state" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Valid From *</Label>
                <Input type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} data-testid="perk-form-valid-from" />
              </div>
              <div>
                <Label>Valid Until *</Label>
                <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="perk-form-valid-until" />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} className="rounded border-gray-300" data-testid="perk-form-featured" />
                Featured
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="rounded border-gray-300" data-testid="perk-form-active" />
                Active
              </label>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#4E50FF] hover:bg-[#3E40EE]" data-testid="perk-form-save-btn">
                {saving ? 'Saving...' : editingPerk ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
